import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Users,
  Building2,
  MapPin,
  UserPlus,
  Eye,
  Key,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";

const CHANNELS = [
  { value: "Retail", label: "Retail / Normal Trade" },
  { value: "GDO", label: "GDO" },
  { value: "DO", label: "DO" },
  { value: "Ho.Re.Ca.", label: "Ho.Re.Ca." },
  { value: "Distributore", label: "Distributore" },
];
const REGIONS = [
  "Lombardia",
  "Lazio",
  "Campania",
  "Sicilia",
  "Veneto",
  "Emilia-Romagna",
  "Piemonte",
  "Puglia",
  "Toscana",
  "Calabria",
  "Sardegna",
  "Liguria",
  "Marche",
  "Abruzzo",
  "Friuli-Venezia Giulia",
  "Trentino-Alto Adige",
  "Umbria",
  "Basilicata",
  "Molise",
  "Valle d'Aosta",
];

const emptyClient = {
  // Dati Azienda
  company_name: "",
  vat_number: "",
  fiscal_code: "",
  sdi_code: "",
  pec: "",
  // Contatto principale
  contact_name: "",
  email: "",
  phone: "",
  // Indirizzo sede
  address: "",
  city: "",
  region: "",
  postal_code: "",
  country: "Italia",
  // Consegna
  delivery_contact: "",
  delivery_address: "",
  delivery_city: "",
  delivery_postal_code: "",
  delivery_notes: "",
  // Commerciale
  channel: "",
  payment_method: "",
  discount_1: 0,
  discount_2: 0,
  discount_3: 0,
  notes: "",
};

export default function ClientsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [formData, setFormData] = useState(emptyClient);

  useEffect(() => {
    fetchClients();
    fetchPaymentMethods();
    if (isAdmin) {
      fetchAgents();
    }
  }, [channelFilter, regionFilter, isAdmin]);

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (channelFilter) params.append("channel", channelFilter);
      if (regionFilter) params.append("region", regionFilter);
      
      const response = await api.get(`/clients?${params}`);
      setClients(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei clienti");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/settings/payment-methods");
      setPaymentMethods(response.data.methods || []);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get("/agents");
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, formData);
        toast.success("Cliente aggiornato con successo");
      } else {
        await api.post("/clients", formData);
        toast.success("Cliente creato con successo");
      }
      setDialogOpen(false);
      setEditingClient(null);
      setFormData(emptyClient);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!selectedClient || !newPassword) return;
    
    try {
      // Fetch all users to check existence
      // Note: In a larger app, this should be a backend search endpoint.
      const userRes = await api.get("/users");
      const targetUser = userRes.data.find(u => u.email === selectedClient.email);
      
      if (targetUser) {
        // User exists -> Update Password
        await api.put(`/users/${targetUser.id}/password`, { password: newPassword });
        toast.success("Password utente aggiornata con successo");
      } else {
        // User does not exist -> Create it
        const confirmCreate = window.confirm(
          `Nessun account utente trovato per ${selectedClient.email}.\n\nVuoi creare un nuovo accesso B2B per questo cliente con la password inserita?`
        );
        
        if (!confirmCreate) return;

        await api.post("/auth/register", {
          email: selectedClient.email,
          password: newPassword,
          name: selectedClient.company_name,
          role: "client",
          client_id: selectedClient.id
        });
        toast.success("Nuovo account B2B creato e collegato al cliente");
      }

      setPasswordDialogOpen(false);
      setNewPassword("");
      setSelectedClient(null);
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error(error.response?.data?.detail || "Errore nell'operazione");
    }
  };

  const openPasswordDialog = (client) => {
    setSelectedClient(client);
    setNewPassword("");
    setPasswordDialogOpen(true);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      company_name: client.company_name,
      vat_number: client.vat_number || "",
      fiscal_code: client.fiscal_code || "",
      sdi_code: client.sdi_code || "",
      pec: client.pec || "",
      contact_name: client.contact_name,
      email: client.email,
      phone: client.phone,
      address: client.address,
      city: client.city,
      region: client.region,
      postal_code: client.postal_code || "",
      country: client.country || "Italia",
      delivery_contact: client.delivery_contact || "",
      delivery_address: client.delivery_address || "",
      delivery_city: client.delivery_city || "",
      delivery_postal_code: client.delivery_postal_code || "",
      delivery_notes: client.delivery_notes || "",
      channel: client.channel,
      payment_method: client.payment_method || "",
      discount_1: client.discount_1 || 0,
      discount_2: client.discount_2 || 0,
      discount_3: client.discount_3 || 0,
      notes: client.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo cliente?")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente eliminato");
      fetchClients();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleToggleActive = async (client) => {
    const action = client.is_active === false ? "activate" : "deactivate";
    const actionLabel = action === "activate" ? "attivare" : "disattivare";
    
    if (!window.confirm(`Sei sicuro di voler ${actionLabel} questo cliente?`)) return;
    
    try {
      await api.put(`/clients/${client.id}/${action}`);
      toast.success(`Cliente ${action === "activate" ? "attivato" : "disattivato"} con successo`);
      fetchClients();
    } catch (error) {
      toast.error(`Errore durante ${actionLabel} del cliente`);
    }
  };

  const handleAssign = async () => {
    if (!selectedAgent || !selectedClient) return;
    try {
      await api.put(`/clients/${selectedClient.id}/assign/${selectedAgent}`);
      toast.success("Cliente assegnato con successo");
      setAssignDialogOpen(false);
      setSelectedClient(null);
      setSelectedAgent("");
      fetchClients();
    } catch (error) {
      toast.error("Errore nell'assegnazione");
    }
  };

  const openAssignDialog = (client) => {
    setSelectedClient(client);
    setAssignDialogOpen(true);
  };

  const filteredClients = clients.filter(
    (client) =>
      client.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getChannelBadgeClass = (channel) => {
    const classes = {
      Retail: "channel-retail",
      GDO: "channel-gdo",
      DO: "channel-do",
      "Ho.Re.Ca.": "channel-horeca",
      "Distributore": "bg-indigo-100 text-indigo-800 border-indigo-200"
    };
    return `channel-badge ${classes[channel] || ""}`;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold">Clienti</h1>
          <p className="text-muted-foreground">
            Gestisci la tua base clienti
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-accent hover:bg-accent/90"
              onClick={() => {
                setEditingClient(null);
                setFormData(emptyClient);
              }}
              data-testid="add-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingClient ? "Modifica Cliente" : "Nuovo Cliente"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Dati Azienda</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2 lg:col-span-2">
                    <Label htmlFor="company_name">Ragione Sociale *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                      data-testid="client-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vat_number">Partita IVA</Label>
                    <Input
                      id="vat_number"
                      value={formData.vat_number}
                      onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                      placeholder="IT12345678901"
                      data-testid="client-vat-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fiscal_code">Codice Fiscale</Label>
                    <Input
                      id="fiscal_code"
                      value={formData.fiscal_code}
                      onChange={(e) => setFormData({ ...formData, fiscal_code: e.target.value.toUpperCase() })}
                      data-testid="client-fiscal-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sdi_code">Codice SDI</Label>
                    <Input
                      id="sdi_code"
                      value={formData.sdi_code}
                      onChange={(e) => setFormData({ ...formData, sdi_code: e.target.value.toUpperCase() })}
                      placeholder="XXXXXXX"
                      maxLength={7}
                      data-testid="client-sdi-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pec">PEC</Label>
                    <Input
                      id="pec"
                      type="email"
                      value={formData.pec}
                      onChange={(e) => setFormData({ ...formData, pec: e.target.value })}
                      placeholder="azienda@pec.it"
                      data-testid="client-pec-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Contatto Principale</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Nome Referente *</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      required
                      data-testid="client-contact-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="client-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefono *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="client-phone-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Sede Legale</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2 lg:col-span-2">
                    <Label htmlFor="address">Indirizzo *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      data-testid="client-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">CAP</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      maxLength={5}
                      data-testid="client-cap-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">Città *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                      data-testid="client-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="region">Regione *</Label>
                    <Select value={formData.region} onValueChange={(value) => setFormData({ ...formData, region: value })}>
                      <SelectTrigger data-testid="client-region-select">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map((region) => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Dati Consegna (se diversi)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="delivery_contact">Contatto Consegna</Label>
                    <Input
                      id="delivery_contact"
                      value={formData.delivery_contact}
                      onChange={(e) => setFormData({ ...formData, delivery_contact: e.target.value })}
                      data-testid="client-delivery-contact-input"
                    />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <Label htmlFor="delivery_address">Indirizzo Consegna</Label>
                    <Input
                      id="delivery_address"
                      value={formData.delivery_address}
                      onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                      data-testid="client-delivery-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_postal_code">CAP</Label>
                    <Input
                      id="delivery_postal_code"
                      value={formData.delivery_postal_code}
                      onChange={(e) => setFormData({ ...formData, delivery_postal_code: e.target.value })}
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_city">Città</Label>
                    <Input
                      id="delivery_city"
                      value={formData.delivery_city}
                      onChange={(e) => setFormData({ ...formData, delivery_city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 lg:col-span-3">
                    <Label htmlFor="delivery_notes">Note Consegna</Label>
                    <Input
                      id="delivery_notes"
                      value={formData.delivery_notes}
                      onChange={(e) => setFormData({ ...formData, delivery_notes: e.target.value })}
                      placeholder="Es. Consegna ore 9-12, citofono magazzino"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Condizioni Commerciali</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="channel">Canale *</Label>
                    <Select value={formData.channel} onValueChange={(value) => setFormData({ ...formData, channel: value })}>
                      <SelectTrigger data-testid="client-channel-select">
                        <SelectValue placeholder="Seleziona canale" />
                      </SelectTrigger>
                      <SelectContent>
                        {CHANNELS.map((channel) => (
                          <SelectItem key={channel.value} value={channel.value}>{channel.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payment_method">Modalità Pagamento</Label>
                    <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                      <SelectTrigger data-testid="client-payment-select">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentMethods.map((pm) => (
                          <SelectItem key={pm.id} value={pm.name}>{pm.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="discount_1">Sconto 1 (%)</Label>
                    <Input
                      id="discount_1"
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={formData.discount_1}
                      onChange={(e) => setFormData({ ...formData, discount_1: parseFloat(e.target.value) || 0 })}
                      data-testid="client-discount1-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="discount_2">Sconto 2 (%)</Label>
                    <Input
                      id="discount_2"
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={formData.discount_2}
                      onChange={(e) => setFormData({ ...formData, discount_2: parseFloat(e.target.value) || 0 })}
                      data-testid="client-discount2-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="discount_3">Sconto 3 (%)</Label>
                    <Input
                      id="discount_3"
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={formData.discount_3}
                      onChange={(e) => setFormData({ ...formData, discount_3: parseFloat(e.target.value) || 0 })}
                      data-testid="client-discount3-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label htmlFor="notes">Note</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="client-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="client-submit-btn">
                  {editingClient ? "Aggiorna" : "Crea"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca clienti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-clients-input"
              />
            </div>
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-channel">
                <SelectValue placeholder="Canale" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i canali</SelectItem>
                {CHANNELS.map((channel) => (
                  <SelectItem key={channel.value} value={channel.value}>
                    {channel.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-region">
                <SelectValue placeholder="Regione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le regioni</SelectItem>
                {REGIONS.map((region) => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun cliente trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codice</TableHead>
                    <TableHead>Azienda</TableHead>
                    <TableHead className="hidden md:table-cell">Contatto</TableHead>
                    <TableHead className="hidden lg:table-cell">Email</TableHead>
                    <TableHead>Canale</TableHead>
                    {isAdmin && <TableHead>Stato</TableHead>}
                    <TableHead className="hidden sm:table-cell">Regione</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow 
                      key={client.id} 
                      data-testid={`client-row-${client.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/clients/${client.id}`)}
                    >
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {client.client_code || "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{client.company_name}</p>
                            <p className="text-sm text-muted-foreground md:hidden">
                              {client.contact_name}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {client.contact_name}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {client.email}
                      </TableCell>
                      <TableCell>
                        <span className={getChannelBadgeClass(client.channel)}>
                          {client.channel}
                        </span>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          {client.is_active === false ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                              In attesa
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                              Attivo
                            </Badge>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          {client.region}
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`client-menu-${client.id}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(client)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            {isAdmin && (
                              <DropdownMenuItem
                                onClick={() => openAssignDialog(client)}
                              >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Assegna Agente
                              </DropdownMenuItem>
                            )}
                            {isAdmin && (
                              <DropdownMenuItem
                                onClick={() => handleToggleActive(client)}
                              >
                                {client.is_active === false ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                    Attiva Cliente
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-4 h-4 mr-2 text-amber-600" />
                                    Disattiva Cliente
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openPasswordDialog(client)}>
                              <Key className="w-4 h-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(client.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password Cliente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label>Nuova Password per {selectedClient?.company_name}</Label>
              <Input
                required
                type="password"
                placeholder="Minimo 8 caratteri"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Questa azione cambierà la password dell'utente associato a {selectedClient?.email}.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-accent hover:bg-accent/90">
                Salva Nuova Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna Cliente a Agente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Assegna {selectedClient?.company_name} a un agente
            </p>
            <Select value={selectedAgent} onValueChange={setSelectedAgent}>
              <SelectTrigger data-testid="assign-agent-select">
                <SelectValue placeholder="Seleziona agente" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setAssignDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button
                onClick={handleAssign}
                className="bg-accent hover:bg-accent/90"
                disabled={!selectedAgent}
                data-testid="assign-confirm-btn"
              >
                Assegna
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
